// this holds custom form non-global components

import kebabCase from 'lodash/kebabCase'
//import cloneDeep from 'lodash/cloneDeep'
//import toNumber from 'lodash/toNumber'
import Cleave from 'cleave.js'
import { DateTime } from 'luxon';

/*
RULES FOR YOUR HotEditInput form inputs
- all should implement a checkValidity() method that can be called from parent - returns true/false
- all should implement a checkInvalidMessage() method that returns string of error if its invalid, or empty string if it is valid -- can be checked using ref from parent
*/

var HotEditInput = {
    name: 'HotEditInput',
    inheritAttrs: false,
    props: {
        value: [String, Number],
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // can set to 'auto', can also override by setting label-vertical
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        labelSize: {  // if you don't provide this, it will use size from 'size' prop
            type: String,
            required: false,
        },
        size: {
            type: String,
            default: 'md'
        },
        type: {  // options are text, email, url, number, search, password, tel, date, time, range, color
            type: String,
            default: 'text',
        },
        placeholder: {  // placeholder text in the input if no value
            type: String,
            required: false,
        },
        emptyText: {  // empty text to show if not in edit mode, and there's no value
            type: String,
            default: '---',
        },
        autocomplete: {  // sets the autocomplete on b-form-input, which sets HTML5 autocomplete value on the input box.  
            //our default is 'nope', which is just a random string since Chrome ignores 'off' most of the time
            type: String,
            default: 'nope',
        },
        loading: { // optional... if set, it force-shows a loading placeholder in the input
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        },
    },
    data: function() {
        return {
            localState: null,  // used to control validity styling of form-group -- can be true, false, or null
            // make sure you implement a method that actually sets this! -- start with null, then on @blur set it
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleEmit: function(event) {
            // using this 'relay' method triggered by @input on the b-form... component seems to be the best way
            // previously tried doing @input.native but then it doesn't take advantage of all b-form... component features 
            // this way worked best - just do @input="handleEmit" on b-form... component
            this.$emit('input', event);
        },
        handleBlur: function(event) {
            this.$emit('blur', event); // using this to pass up the blur event, in case parent needs it
            this.$nextTick(() => {  // need nextTick, or else it shows invalid before things update
                this.checkValidity();   // this is here just because we want it to happen during blur event also
            });
        },
        handleEnter: function(event) {
            // called when user hits enter when in this input
            // just emit some standard event so you can hook into it from parent
            this.$emit('keyup-enter', event);
        },
        checkValidity: function() {
            // made this so we can split actual boolean validity check and error message reporting
            let inputBox = this.$refs.inputBox;  // this will only be here if we're in edit mode... so if not in edit mode, we need to check validity another way, or just return that its OK
            let inputBoxValid = inputBox ? inputBox.checkValidity() : null;
            this.localState = inputBoxValid ? null : inputBoxValid; // this will only return null or false... so we only show errors, not non-errors
            return inputBoxValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const standardErrorMessage = this.label + ': ' + 'Please enter a valid value.';
            const urlErrorMessage = this.label + ': ' + 'Please make sure this is a complete URL, with http://...';
            const emailErrorMessage = this.label + ': ' + 'Please enter a valid email address.';
            let errorMessage = standardErrorMessage;
            if (this.type === 'url') errorMessage = urlErrorMessage;
            if (this.type === 'email') errorMessage = emailErrorMessage;
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                class="hot-edit-input-group hot-edit-input mb-0"
                :description="description"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-size="labelSize || size"
                :label-for="'input-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <b-skeleton v-if="loading" width="70%"></b-skeleton>
                    <div v-if="!loading && (value || emptyText)" :class="!!value ? '' : 'text-muted'">
                        <a v-if="type === 'url' && !!value" :href="value" target="_blank">{{value || emptyText}}</a>
                        <a v-else-if="type === 'email' && !!value" :href="'mailto:' + value">{{value || emptyText}}</a>
                        <span v-else>{{ value || emptyText }}</span>
                    </div>
                </div>
                
                <b-form-input
                    v-else
                    key="edit"
                    v-bind="$attrs"
                    :id="'input-' + idSuffix"
                    ref="inputBox"
                    :required="required"
                    :type="type"
                    v-bind:value="value"
                    @input="handleEmit" 
                    @blur="handleBlur"
                    @keyup.enter="handleEnter"
                    :state="localState"
                    trim
                    :disabled="disabled"
                    :autocomplete="autocomplete"
                    :placeholder="loading ? 'Loading...' : placeholder"
                    :size="size"
                    :plaintext="!localEditMode"
                    :class="inputClass"
                ></b-form-input>
            </b-form-group>
        </div>
    `
}

var HotEditDiv = {
    // this is just like HotEditInput, but it never goes into edit mode, and its default slot holds the content, don't use value
    // use no-edit-class to apply css classes to the default slot area
    name: 'HotEditDiv',
    inheritAttrs: false,
    props: {
        value: {
            type: [String, Number],
            required: false,
        },
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // can set to 'auto', can also override by setting label-vertical
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        size: {
            type: String,
            default: 'md'
        },
        type: {  // options are text, email, url, number, search, password, tel, date, time, range, color
            type: String,
            default: 'text',
        },
        placeholder: {  // placeholder text in the input if no value
            type: String,
            required: false,
        },
        autocomplete: {  // sets the autocomplete on b-form-input, which sets HTML5 autocomplete value on the input box.  
            //our default is 'nope', which is just a random string since Chrome ignores 'off' most of the time
            type: String,
            default: 'nope',
        },
        loading: { // optional... if set, it force-shows a loading placeholder in the input
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        },
    },
    data: function() {
        return {
            localState: null,  // used to control validity styling of form-group -- can be true, false, or null
            // make sure you implement a method that actually sets this! -- start with null, then on @blur set it
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            return false;  // always do this for this component
        },
    },
    methods: {
        
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                class="hot-edit-input-group hot-edit-input mb-0"
                :description="description"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-size="size"
                :label-for="'input-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <b-skeleton v-if="loading" width="70%"></b-skeleton>
                    <div v-if="!loading">
                        <slot>Default Content</slot>
                    </div>
                </div>
                
                <b-form-input
                    v-else
                    key="edit"
                    v-bind="$attrs"
                    :id="'input-' + idSuffix"
                    ref="inputBox"
                    :required="required"
                    :type="type"
                    v-bind:value="value"
                    @input="handleEmit" 
                    @blur="handleBlur"
                    @keyup.enter="handleEnter"
                    :state="localState"
                    trim
                    :autocomplete="autocomplete"
                    :placeholder="loading ? 'Loading...' : placeholder"
                    :size="size"
                    :plaintext="!localEditMode"
                    :class="inputClass"
                ></b-form-input>
            </b-form-group>
        </div>
    `
}


var ClickEditInput = {
    name: 'ClickEditInput',
    inheritAttrs: false,
    props: {
        value: [String, Number],
        label: {
            type: String,
            required: true,
        },
        placeholder: {  // for this one, placeholder text is used when in edit mode or not... for now
            type: String,
            required: false,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {  // used to FORCE this into edit mode from the parent
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        hideLabel: {  // hide the form label by adding .sr-only to label class
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        autocomplete: {  // sets the autocomplete on b-form-input, which sets HTML5 autocomplete value on the input box.  our default is 'off' so its less annoying
            type: String,
            default: 'off',
        },
        type: {  // input type
            type: String,
            default: 'text',
        },
        loading: { // optional... if set, it force-shows a loading placeholder in the input
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        },
        noEditClass: {  // string of class(es) to use wrapping the value when NOT in edit mode
            type: String,
            default: '',
        },
    },
    data: function() {
        return {
            clickEditMode: false,
            localValue: '',  // v-model from input should modify this, then if we save, we emit this value
            localState: null,  // set this in your checkValidity() method, which you must call on blur or something
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.clickEditMode || this.editMode;
            }
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        enterEditMode: function() {
            this.clickEditMode = true;
            this.localValue = this.value;  // copy prop value to localValue, then modify that
        },
        handleBlur: function(event) {
            this.$emit('blur', event);
            this.handleSave();
        },
        handleSave: function() {
            let isValid = this.checkValidity();
            this.$emit('input', this.localValue);
            if (isValid === false) return;  // if it is not valid, then block exiting editMode 
            // otherwise proceed if validityCheck is null or true
            this.clickEditMode = false;
        },
        handleCancel: function() {
            this.localValue = this.value;  // reset localValue to original value passed in as prop
            this.clickEditMode = false;
        },
        checkValidity: function() {
            let inputBoxValid = this.$refs.inputBox.checkValidity();
            this.localState = inputBoxValid ? null : inputBoxValid; // this will only return null or false... so we only show errors, not non-errors
            return inputBoxValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Please enter some text';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                :description="description"
                class="hot-edit-input-group click-edit-input-group click-edit-input mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'input-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :label-sr-only="hideLabel"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <b-skeleton v-if="loading" key="loading" type="input" width="50%"></b-skeleton>
                <span
                    v-else-if="!localEditMode"
                    key="noedit"
                    class="mb-0"
                >
                    <span
                        class="underline-dashed spl-inputs-minimal"
                        :class="[value ? '' : 'text-muted', noEditClass]"
                        @click="enterEditMode"
                    >
                        {{ value ? value : placeholder }}
                    </span>
                    <b-button 
                        v-if="!noEdit"
                        variant="minimal-secondary" 
                        size="sm"
                        class="ml-2"
                        @click="enterEditMode"
                    >
                        <b-icon icon="pencil"></b-icon>
                    </b-button>
                </span>
                
                <b-input-group
                    v-else
                    key="edit"
                >
                    <b-form-input
                        v-bind="$attrs"
                        :id="'input-' + idSuffix"
                        ref="inputBox"
                        :type="type"
                        v-model="localValue"
                        trim
                        :placeholder="placeholder"
                        :autocomplete="autocomplete"
                        :plaintext="!localEditMode"
                        :class="inputClass"
                        class="flex-grow-1"
                        :state="localState"
                        :disabled="disabled"
                        @keyup.enter="handleBlur"
                        @blur="handleBlur"
                    ></b-form-input>
                    <b-input-group-append
                        v-if="!editMode"
                    >
                        <b-button
                            variant="soft-success" 
                            @click="handleSave"
                        >
                            <b-icon icon="check"></b-icon>
                        </b-button>
                        <b-button
                            variant="soft-danger"
                            @click="handleCancel"
                        >
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
        </div>
    `
}

var HotEditTextarea = {
    name: 'HotEditTextarea',
    inheritAttrs: false,
    props: {
        value: [String, Number],
        label: {
            type: String,
            required: true,
        },
        placeholder: {  // placeholder text in the input if no value in EDIT MODE
            type: String,
            required: false,
        },
        emptyText: {  // empty text to show if NOT in edit mode, and there's no value
            type: String,
            default: '---',
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        },
    },
    data: function() {
        return {
            localState: null,  // set this during checkValidity() which you should call on @blur
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleEmit: function(event) {
            // using this 'relay' method triggered by @input on the b-form... component seems to be the best way
            // previously tried doing @input.native but then it doesn't take advantage of all b-form... component features 
            // this way worked best - just do @input="handleEmit" on b-form... component
            this.$emit('input', event);
        },
        handleBlur: function(event) {
            this.$emit('blur', event);
            this.checkValidity();
        },
        checkValidity: function() {
            let textareaValid = this.$refs.textarea.checkValidity();
            this.localState = textareaValid ? null : textareaValid; // this will only return null or false... so we only show errors, not non-errors
            return textareaValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Please enter some text';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                :description="description"
                class="hot-edit-input-group hot-edit-textarea mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'textarea-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>
            
                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <div v-if="loading">
                        <b-skeleton width="80%"></b-skeleton>
                        <b-skeleton width="80%"></b-skeleton>
                        <b-skeleton width="40%"></b-skeleton>
                    </div>
                    <div v-if="!loading && (value || emptyText)" :class="!!value ? '' : 'text-muted'"">
                        <span class="text-show-breaks">{{ value || emptyText }}</span>
                    </div>
                </div>
                
                <b-form-textarea
                    v-else
                    key="edit"
                    v-bind="$attrs"
                    :id="'textarea-' + idSuffix"
                    ref="textarea"
                    :placeholder="loading ? 'Loading...' : placeholder"
                    :required="required"
                    v-bind:value="value"
                    @input="handleEmit" 
                    @blur="handleBlur"
                    :state="localState"
                    trim
                    :disabled="disabled"
                    :plaintext="!localEditMode"
                    :class="inputClass"
                ></b-form-textarea>
            </b-form-group>
        </div>
    `
}

var HotEditSwitch = {
    name: 'HotEditSwitch',
    inheritAttrs: false,
    props: {
        value: Boolean,
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        switchSize: {
            type: String,
            default: 'md',
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        // DO NOT ADD REQUIRED TO THIS - DOES NOT MAKE SENSE FOR CHECKBOXES
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        }
    },
    data: function() {
        return {
            localState: null,  // setting this to null since we don't actually check it... but we need to make sure it has this property to be consistent with other HotEditInputs
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        textValue: function() {
            if (this.value === true) {
                return 'Yes';
            } else if (this.value === false) {
                return 'No';
            }
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        finalInputClasses: function() {  // you might not need this... std (no-outline) thing won't ever get applied anyways since this will be hidden if !localEditMode
            // combine ones from inputClass and the !localEditMode one
            let std = {'no-outline': !this.localEditMode};  // always put this in there
            if (!this.inputClass) return std;  // if nothing else, return that
            if (this.inputClass && Array.isArray(this.inputClass)) {  // check if inputClass is an array
                return [std, ...this.inputClass];  // combine them in Array format, object format can be inside Array format just fine
            }
            if (this.inputClass && typeof this.inputClass === 'string') {  // check if inputClass is a string, if so, split into array
                let splitClasses = this.inputClass.split(' ');
                return [std, ...splitClasses];
            }
            // otherwise it must be an object... ?
            return Object.assign(std, this.inputClass);  // merge them and return the target (Object.assign returns the target)
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleChange: function(payload) {
            // need to actually emit 'input' event because that's what parent will expect when it has v-model with value
            this.$emit('input', payload);
        },
        checkValidity: function() {
            // always return true here so we don't cause problems checking from parent
            return true;
        },
        checkInvalidMessage: function() {
            let valid = this.checkValidity();
            return valid;  // doing this just to make sure the method exists for consistency with other HotEditInputs
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                
                class="hot-edit-input-group hot-edit-switch mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'switch-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
            >
                <template #label>
                    {{ label }}
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <b-skeleton v-if="loading" width="30%"></b-skeleton>
                    <div v-if="!loading && textValue">
                        {{ textValue }}
                    </div>
                </div>
                
                <b-form-checkbox
                    v-else
                    key="edit"
                    v-bind="$attrs"
                    switch
                    ref="switch"
                    :size="switchSize"
                    :disabled="disabled"
                    :id="'switch-' + idSuffix"
                    v-bind:checked="value"
                    @change="handleChange"
                    :class="finalInputClasses"
                ></b-form-checkbox>

                <template #description></template>

            </b-form-group>
            
            <div class="small text-muted">
                {{ description }}
            </div>
        
        </div>
    `
}
// v-on:change.native="$emit('input', $event.target.checked)" 

var HotEditSelect = {
    name: 'HotEditSelect',
    inheritAttrs: false,
    props: {
        value: [String, Number],
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        options: {
            type: Array,
            default: function() {
                return [];
            },
        },
        blankOption: {  // OPTIONAL - if you want a SELECTABLE blank option, enter text for it here - value will be empty string
            // this will be selectable (not disabled)  Only do this if your value is not required
            // this is different than the emptyPlaceholder... that's always there, and uses value of null
            // this uses value of "" empty string and text provided
            type: String,
            required: false,
        },
        emptyPlaceholder: {  // this is only displayed if you DO NOT have a blankOption -- shown IN THE INPUT when in EDIT MODE
            type: String,
            default: 'Select one...',
        },
        emptyText: {  // shown only when NOT in edit mode, if no value is selected
            type: String,
            default: '---',
        },
        valueField: {
            type: String,
            default: 'value',
        },
        textField: {
            type: String,
            default: 'text',
        },
        disabledField: {  // the key in options object to use for 'disabled'
            type: String,
            default: 'disabled',
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        }
    },
    data: function() {
        return {
            localState: null,  // set this during checkValidity()
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        hasBlankOption: function() {
            return this.blankOption ? true : false;
        },
        /*
        localOptions: function() {
            // see comments above regarding addBlankOption
            // careful not to mutate Options... it might mutate on the parent!
            if (this.blankOption) {
                const blank = {value: "", text: this.blankOption};
                const copyOptions = cloneDeep(this.options);
                copyOptions.unshift(blank);
                return copyOptions;
            }
            return this.options;
        },*/
        currentValueText: function() {
            if (this.value && this.options.length > 0) {
               return this.options.find(opt => opt[this.valueField] === this.value)[this.textField];
            } else {
                return this.value;
            }
        },
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleChange: function(event) {
            // using this 'relay' method triggered by @change on the b-form-select seems to be the best way
            // we are only acting on @change events so we only update on user input
            // but we are emitting 'input' events so the v-model updates correctly on parent
            this.$emit('input', event);
            this.$nextTick(() => {  // had to bury this in 2 nextTicks to get it to work right
                this.$nextTick(() => {
                    this.checkValidity();
                });
            });
        },
        checkValidity: function() {
            // trying a few different ways... pick one
            //let selectValid = this.required ? !!this.$refs.selectBox.value : true;  // this is my crappy way of checking -- this had issues getting updated because we had to bury reactivity in multiple nextTicks
            let nativeInputValid = this.$refs.selectBox.$refs.input.validity.valid;  // checking native validity of the buried native input -- this works
            this.localState = nativeInputValid ? null : nativeInputValid; // this will only return null or false... so we only show errors, not non-errors
            return nativeInputValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Please make a valid selection.';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                :description="description"
                class="hot-edit-input-group hot-edit-select mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'select-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >   
                    <b-skeleton v-if="loading" width="70%"></b-skeleton>
                    <div v-if="!loading && (currentValueText || emptyText)" :class="!!currentValueText ? '' : 'text-muted'">
                        {{ currentValueText || emptyText }}
                    </div>
                </div>

                <b-form-select
                    v-else
                    v-bind="$attrs"
                    key="edit"    
                    :id="'select-' + idSuffix"
                    ref="selectBox"
                    :required="required"
                    v-bind:value="value"
                    @change="handleChange" 
                    :state="localState"
                    :options="options"
                    :value-field="valueField"
                    :text-field="textField"
                    :disabled-field="disabledField"
                    :class="[inputClass, {'loading': loading}]"
                    :disabled="loading || disabled"
                >
                    <template v-slot:first>
                        <b-form-select-option v-if="loading" value="">
                            Loading...
                        </b-form-select-option>
                        <b-form-select-option v-else-if="hasBlankOption" value="">{{ blankOption }}</b-form-select-option>
                        <b-form-select-option v-else value="" disabled>{{ emptyPlaceholder }}</b-form-select-option>
                    </template>
                </b-form-select>

            </b-form-group>
        </div>
    `
}

var HotEditCheckboxGroup = {
    name: 'HotEditCheckboxGroup',
    inheritAttrs: false,
    props: {
        // we will still use 'value' at top level of component, but will pass to checkbox group as 'checked'
        value: {  // value should be array of strings, OPTIONS should be objects with { value, text, disabled } or use value-field and text-field and disabled-field props
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        noValueText: {  // shown only when NOT in edit mode, if no value is selected
            type: String,
            default: '---',
        },
        noOptionsText: {  // shown only when IN edit mode, if no options available
            type: String,
            default: 'No Options',
        },
        options: {
            type: Array,
            default: function() {
                return [];
            },
        },
        valueField: {
            type: String,
            default: 'value',
        },
        textField: {
            type: String,
            default: 'text',
        },
        disabledField: {
            type: String,
            default: 'disabled',
        },
        size: {  // size of boxes
            type: String,
            default: 'md',
        },
        stacked: {  // shows checkboxes stacked vertically
            type: Boolean,
            default: false,
        },
        switches: {  // shows switches instead of checkbox
            type: Boolean,
            default: false,
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        }
    },
    data: function() {
        return {
            localState: null,  // set this during checkValidity() - this is NOT the local value, but the local validity status basically
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        selectedOptions() {
            // full objects of selected options - not just values
            if (this.value && Array.isArray(this.value) && (this.value.length > 0) && this.options.length > 0) {
                // value will likely just be the IDs of things -- the valueField of objects
                return this.options.filter(opt => this.value.includes(opt[this.valueField]));
            } else {
                return [];
            }
        },
        currentValueText: function() {
            if (this.value && Array.isArray(this.value) && (this.value.length > 0) && this.options.length > 0) {
                // value will likely just be the IDs of things -- the valueField of objects
                const selectedOptionsText = this.options.filter(opt => this.value.includes(opt[this.valueField])).map(opt => opt[this.textField]);
                // that's an array of strings of text values - now join for display
                return selectedOptionsText.join(', ')
            } else {
                // if no value, then just return null so template will show noValueText instead
                // return this.value;
                return null;
            }
        },
        noOptions() {
            // do we have no selected value?
            return !this.options || !Array.isArray(this.options) || (this.options.length < 1);
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleChange: function(event) {
            // triggering this on change so it only goes based on user input
            // but we are emitting 'input' events so the v-model updates correctly on parent
            this.$emit('input', event);
            this.checkValidity();
            /* this.$nextTick(() => {  // had to bury this in 2 nextTicks to get it to work right on select...
                this.$nextTick(() => {
                    this.checkValidity();
                });
            }); */
        },
        checkValidity: function() {
            // trying a few different ways... pick one
            //let selectValid = this.required ? !!this.$refs.checkboxGroup.value : true;  // this is my crappy way of checking -- this had issues getting updated because we had to bury reactivity in multiple nextTicks
            // OLD... from select
            //let nativeInputValid = this.$refs.checkboxGroup.$refs.input.validity.valid;  // checking native validity of the buried native input -- this works
            //this.localState = nativeInputValid ? null : nativeInputValid; // this will only return null or false... so we only show errors, not non-errors
            //return nativeInputValid;
            // we will just check if there is at least one selected, if its required -- can do more later
            const hasAtLeastOne = !!this.value && Array.isArray(this.value) && (this.value.length > 0);
            // this.localState should only be null or false... so we only show errors, not non-errors
            if (this.required) {
                this.localState = hasAtLeastOne ? null : false;  // so we only show null or false, not true -- only want to show errors
            }
            return this.required ? hasAtLeastOne : true;  // the actual validity

        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Please make a valid selection.';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                :description="description"
                class="hot-edit-input-group hot-edit-checkbox-group mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'checkbox-group-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>
                
                <template #description>
                    <div class="small text-muted">{{ description }}</div>
                </template>
                
                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >   
                    <slot name="no-edit" v-bind="{ currentValueText, noValueText, selectedOptions }">
                        <b-skeleton v-if="loading" width="70%"></b-skeleton>
                        <div v-if="!loading && (currentValueText || noValueText)" :class="!!currentValueText ? '' : 'text-muted'">
                            {{ currentValueText || noValueText }}
                        </div>
                    </slot>
                </div>

                <b-form-checkbox-group
                    v-else
                    v-bind="$attrs"
                    key="edit"
                    :id="'checkbox-group-' + idSuffix"
                    ref="checkboxGroup"
                    :required="required"
                    v-bind:checked="value"
                    @change="handleChange" 
                    :state="localState"
                    :options="options"
                    :value-field="valueField"
                    :text-field="textField"
                    :disabled-field="disabledField"
                    :size="size"
                    :stacked="stacked"
                    :switches="switches"
                    :class="[inputClass, {'loading': loading}]"
                    :disabled="loading || disabled"
                ></b-form-checkbox-group>

                <div
                    v-if="!loading && localEditMode && noOptions"
                >
                    {{ noOptionsText }}
                </div>

            </b-form-group>
        </div>
    `
}

var HotEditDatepicker = {
    name: 'HotEditDatepicker',
    inheritAttrs: false,  // you can still pass through, they will go to datepicker component
    props: {
        value: [String, Date, Object],  // Nov 2021 - added Object so we could pass in DateTime objects too
        label: {
            type: String,
            required: true,
        },
        description: {  // description or help text, only shown during edit mode
            type: String,
            required: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dateFormatOptions: {
            type: Object,
            default: function() {
                return { year: 'numeric', month: 'numeric', day: 'numeric' };
            },
        },
        showResetButton: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        inputClass: {  // String, Array, or Object class(es) to pass on to BS-Vue input element
            type: [String, Array, Object],
            required: false,
        }
    },
    data: function() {
        return {
            localState: null,  // set this during checkValidity() which you should call on changes, etc.
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
    },
    filters: {
        luxonDate (value) {
            // use Luxon to go string to DT to string
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            let formatOptions = this.dateFormatOptions || DateTime.DATE_HUGE;
            //return dt.toLocaleString(DateTime.DATE_HUGE);
            return dt.toLocaleString(formatOptions);
        },
        luxonTime (value) {
            // use Luxon to go string to DT to string
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return dt.toLocaleString(DateTime.TIME_SIMPLE);
        },
        luxonTimeZone (value) {
            // use Luxon to generate time zone name
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return dt.zoneName;
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleInputEvent: function(value) {
            this.$emit('input', value);  // this will be a string 2020-06-11 unless you set prop of value-as-date
            this.$nextTick(() => {
                this.checkValidity();
            });
        },
        checkValidity: function() {
            let datepickerValid = this.required ? !!this.$refs.datepicker.value : true;
            this.localState = datepickerValid ? null : datepickerValid;  // never set to true, only null or false so we don't show green stuff everywhere
            return datepickerValid;
        },
        checkInvalidMessage: function() {
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Please select a valid date';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        },
        formattedDate: function(value) {
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            let formatOptions = this.dateFormatOptions || DateTime.DATE_HUGE;
            //return dt.toLocaleString(DateTime.DATE_HUGE);
            return dt.toLocaleString(formatOptions);
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                :description="description"
                class="hot-edit-input-group hot-edit-datepicker mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'datepicker-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <b-skeleton v-if="loading" width="70%"></b-skeleton>
                    <div v-if="!loading && value">
                        {{ formattedDate(value) }}
                    </div>
                    <div v-else-if="!loading && !value" class="text-muted">None</div>
                </div>

                <b-form-datepicker
                    v-else
                    v-bind="$attrs"
                    key="edit"    
                    ref="datepicker"
                    :id="'datepicker-' + idSuffix"
                    :required="required"
                    :date-format-options="dateFormatOptions"
                    :reset-button="showResetButton"
                    v-bind:value="value"
                    :state="localState"
                    @input="handleInputEvent"
                    :disabled="disabled"
                    :class="inputClass"
                ></b-form-datepicker>

            </b-form-group>
        </div>
    `
}

var HotEditDateTime = {
    // go in and out with ISO string format - safest for working with API
    name: 'HotEditDateTime',
    inheritAttrs: false,  // you can still pass through with v-bind="$attrs" on child component but you have multiple components so be careful
    props: {
        value: {
            type: [String, Object],  // Should be ISO 8601 string so we can parse correctly
        },  // Nov 2021 - added Object so we could pass in DateTime objects from Luxon, and added ability to handle those here
        label: {
            type: String,
            required: true,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        emptyText: {  // text to show when NOT in edit mode and no value is set
            type: String,
            default: 'Not Set',
        },
        defaultTime: {
            type: String,
            default: '17:00:00',  // should be ISO 8601 string
        },
    },
    data: function() {
        return {
            showInvalid: false,  // set to true to render invalid visual state
            invalidMessage: 'Date / Time is not valid',
            localDate: null,  // set when we parsed incoming value & updated from Datepicker
            localTime: null,  // set when we parsed incoming value & updated from Timepicker
            timeZone: null,  // set when we parsed incoming value
            dateTimeIsValid: false,
            setButtonClicked: false,
        }
    },
    filters: {
        luxonDate (value) {
            // use Luxon to go string to DT to string
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return dt.toLocaleString(DateTime.DATE_HUGE);
        },
        luxonTime (value) {
            // use Luxon to go string to DT to string
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return dt.toLocaleString(DateTime.TIME_SIMPLE);
        },
        luxonTimeZone (value) {
            // use Luxon to generate time zone name
            let dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return dt.zoneName;
        },
    },
    /*
    Overall, works like this:
    - value comes in, watcher watches for changes in value prop
    - watcher calls setLocalData to SPLIT the UTC string into date and time and display time zone
    - separate widgets are v-model connected to the split values (which is why we can't use computed value to split things)
    - when @input event happens on each input, we update the local value, then call function to do emit inside nextTick
    - emit value gets Luxon DateTime object (a computed property, hence why we wait for nextTick), makes sure it is valid, then converts to UTC string and emits that
    */
    watch: {
        value: function(newVal) {
            this.setLocalData(newVal);
        },
    },
    mounted: function() {
        // need this because our watch otherwise misses things when created
        this.setLocalData(this.value);
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        hasAnyValue: function() {
            // returns true if value prop is not empty, or if any local values are set
            // used to control syling (grayed out if nothing set)
            return !!this.value || !!this.localDate || !!this.localTime || this.setButtonClicked;
        },
        isValid: function() {
            // is the overall selection valid?  if there is no value at all (!hasAnyValue) then it is valid
            if (!this.hasAnyValue && !this.required) return true;
            if (this.currentDateTimeObj) {
                return this.currentDateTimeObj.isValid; 
            } // if has a value or is required, check the actual local DateTime object to see if its valid
            return false;  // if we hasAnyValue or required, but no DT object, then its not valid
        },
        currentDateTimeObj: function() {
            // takes data properties localDate, localTime, and timeZone and builds it back up into Luxon DT object
            let dtString = this.localDate + 'T' + this.localTime;
            // console.log('dtString from currentDateTimeObj', dtString);
            // we are purposesly NOT specifying a time zone... as long as we split incoming value using Luxon (we did) it will use the same time zone when putthing things back together
            let normalized = DateTime.fromISO(dtString);  // assumes 'local' time zone if not provided in the ISO string...
            // console.log('normalized from currentDateTimeObj', normalized);
            if (!normalized.isValid) {
                // console.log('normalized IS NOT valid');
                return null;
            } else {
                // console.log('normalized IS valid');
            }
            return normalized;  // this is a Luxon DateTime object, but could be null
        },
        defaultTimeText() {
            // return string of defaultTime formatted nicely for display in button
            // we use full DateTime object here so we can use Luxon formatting, but we only care about the time part
            const dt = DateTime.fromISO(this.defaultTime);
            return dt.toLocaleString(DateTime.TIME_SIMPLE);
        },
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset showInvalid (to false) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.showInvalid = false;
        },
        setLocalData: function(propValue) {  // called from watcher, and during mounted hook
            // this takes input value and SPLITS it for each widget
            // then when widgets update, they go through emit handler functions, up to parent
            // then new value comes in through value, gets split... on and on...
            // console.log('incoming propValue from setLocalData', propValue);
            if (propValue) {
                let dt = DateTime.isDateTime(propValue) ? propValue : DateTime.fromISO(propValue);
                this.dateTimeIsValid = dt.isValid ? true : false;
                // if not valid, should we null everything out???
                const localDt = dt.toLocal().setLocale('en');  // same as .setZone('local') but more explicit - and we set the locale to 'en' so time is consistent
                // call dt.toLocal() -- sets the zone to local TZ, which should be what we want here
                this.localDate = localDt.toISODate();  // just the date part, in ISO format, which is what our input datepicker wants
                // NOTE: do not use toLocaleString for time (unless first setting the locale to correct value), it will change to locale formatted string, which can cause problems
                // but we still must pass the 'local' time, since we're also pulling out and using the local TZ
                // but we want the 'local' time, with a fixed 'locale' (or at least a matching locale to what we have in timepicker!)
                // for consistency we'll always pass around time in 'en' locale (but it will be in the local time zone)
                // we are getting ISODate, will time be correct then?
                this.localTime = localDt.toLocaleString(DateTime.TIME_24_WITH_SECONDS);  // make sure this is in consistent locale as the timepicker
                this.timeZone = localDt.zoneName;
                // console.log('setting localDate, localTime, timeZone', this.localDate, this.localTime, this.timeZone);
            }
        },
        handleDateInput: function(dateValue) {
            // console.log('dateValue', dateValue);
            this.localDate = dateValue;
            this.$nextTick(() => {
                this.handleEmit(this.currentDateTimeObj);
            });
        },
        handleTimeInput: function(timeValue) {
            // console.log('timeValue', timeValue);
            this.localTime = timeValue;
            this.$nextTick(() => {
                this.handleEmit(this.currentDateTimeObj);
            });
        },
        handleEmit: function(dateTimeObj) {
            // takes Luxon DT object (maybe null!) and express it as UTC time
            // only emit if its valid, and maybe have to do nextTick?
            if (dateTimeObj && dateTimeObj.isValid) {  // should be valid, we checked in watcher
                let rezoned = dateTimeObj.setZone("utc");
                const utcString = rezoned.toISO();
                // console.log('utcString from handleEmit', utcString);
                this.$emit('input', utcString);
            }
        },
        resetAll: function() {
            this.localDate = '';
            this.localTime = '';
            this.timeZone = '';
            this.dateTimeIsValid = false;
            this.setButtonClicked = false;
            this.$emit('input', null); // make sure you always emit null for 'no date' case... API expects it
        },
        checkValidity: function() {
            this.showInvalid = true;
            return this.isValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref=""
            standard things:  
                isValid computed property  Boolean
                showInvalid data property  Boolean --> shows invalid state visual stuff if isValid = false
                invalidMessage data property String ie: 'Must have a value'
            check isValid, set showValidity data prop to True, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            this.checkValidity();
            const combinedMessage = this.label + ': ' + this.invalidMessage;
            return this.isValid ? '' : combinedMessage;
        },
        handleSetButtonClicked: function() {
            // this is called when the Set Date button is clicked, added to to pre-populate the default time if no time is set
            if (!this.localTime) {
                this.handleTimeInput(this.defaultTime);
            }
            this.setButtonClicked = true;
        },
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                class="hot-edit-input-group hot-edit-date-time mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-for="'datepicker-' + idSuffix"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
            >
                <template #description>
                    <div v-if="localEditMode" class="d-flex justify-content-between align-items-start">
                        <div>    
                            <b-icon v-if="hasAnyValue" :icon="isValid ? 'check-circle' : 'x-circle'" :variant="isValid ? 'success' : 'danger'"></b-icon>
                            {{timeZone ? timeZone : 'Select date/time'}}
                        </div>
                        <div>
                            <b-link :disabled="disabled" @click="resetAll">Clear</b-link>
                        </div>
                    </div>
                </template>

                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <div v-if="loading" key="noEditLoading">
                        <b-skeleton width="70%"></b-skeleton>
                        <b-skeleton width="35%"></b-skeleton>
                    </div>
                    <div v-if="!loading && value" key="noEditLoading">
                        {{ value | luxonDate }} <span class="text-muted">at</span> {{ value | luxonTime }}
                        <br>
                        <span class="small text-muted">{{ value | luxonTimeZone }}</span>
                    </div>
                    <div v-else-if="!loading" class="text-muted">{{ emptyText }}</div>
                </div>

                <div 
                    v-else 
                    key="edit" 
                    class="input-wrapper"
                    :class="{
                        'inactive-form-control': !hasAnyValue,
                        'border-danger': showInvalid && !isValid,
                    }"
                >
                    <div v-if="!hasAnyValue">
                        <b-button 
                            block 
                            :disabled="disabled"
                            variant="soft-secondary"
                            @click="handleSetButtonClicked"
                        >
                            <b-spinner small v-if="loading"></b-spinner>
                            <span v-if="!loading">Set Date</span>
                        </b-button>
                    </div>
                    <div v-if="hasAnyValue">
                        <b-form-datepicker
                            class="border-0 no-outline"
                            :id="'datepicker-' + idSuffix"
                            ref="datepicker"
                            :value="localDate"
                            @input="handleDateInput"
                            label-no-date-selected="Select date"
                            menu-class="shadow"
                            :disabled="disabled"
                        ></b-form-datepicker>

                        <b-form-timepicker
                            :value="localTime"
                            @input="handleTimeInput" 
                            ref="timepicker"
                            locale="en"
                            minutes-step="15"
                            menu-class="shadow"
                            class="border-0 no-outline"
                            reset-button
                            :disabled="disabled"
                            label-no-time-selected="Select time"
                            :label-reset-button="defaultTimeText"
                            :reset-value="defaultTime"
                        ></b-form-timepicker>
                    </div>
                
                </div>

            </b-form-group>
        </div>
    `
}

var HotEditMoney = {
    // setup to use multiple .sync props instead of one v-model
    // with .sync, you need to handle emitting events correctly, but its the only way to do 2-way binding on multiple props
    // handle emitting events like this:  this.$emit('update:title', newTitle)
    name: 'HotEditMoney',
    inheritAttrs: false,
    props: {
        label: {  // label for the field
            type: String,
            required: true,
        },
        amount: {    // the AMOUNT of money - use .sync modifier -- TODO - need to update this to handle decimal_places and max_digits
            type: [String, Number],
            default: '',
        },   
        currency: {  // the 3-letter CURRENCY CODE -- use .sync modifier
            type: String,
            default: 'USD',
        },
        currencyFixed: {  // if true, then user cannot select currency
            type: Boolean,
            default: false,
        },
        currencyOptions: {  // array of strings with 3-digit currency choices, or array of objects with { value: 'USD', text: 'US Dollar' }
            type: Array,
            default: function() {
                return ['USD', 'CNY', 'EUR', 'GBP']
            }
        },
        locale: {  // to set based on user preference sometime later
            type: String,
            default: 'en-US'
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {  
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then neither we never go into editMode
            type: Boolean,
            default: false,
        },
        emptyText: {  // text/number to show when NOT in edit mode, and there's no value
            type: String,
            default: '---',
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // set to null to make vertical layout
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        loading: { // optional... 
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        }
    },
    data: function() {
        return {
            amountState: null,
            currencyState: null,
            localState: null,  // both amount and currency are combined here, will be null or false
        }
    },
    computed: {
        idSuffix: function() {
            return kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        amountFormatted: function() {
            const currencyFormat = {
                style: 'currency',
                currency: this.currency,
            };
            if (this.amount) {
                return parseFloat(this.amount).toLocaleString(this.locale, currencyFormat);
            } else {
                return '';
            }
        },
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState, currencyState, amountState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
            this.currencyState = null;
            this.amountState = null;
        },
        updateAmount: function(rawAmountValue) {
            this.checkValidity();
            const cleanAmountValue = rawAmountValue ? rawAmountValue : null;  // return null if value is blank to avoid problems with empty strings and numbers
            this.$emit('update:amount', cleanAmountValue);
        },
        updateCurrency: function(rawCurrencyValue) {
            this.checkValidity();
            const cleanCurrencyValue = rawCurrencyValue;  // no cleaning to do I think
            this.$emit('update:currency', cleanCurrencyValue);
        },
        checkValidity: function() {
            let amountValid = this.$refs.amount.checkValidity();
            this.amountState = amountValid ? null : false;
            let currencyValid = this.required ? !!this.$refs.currency.value : true;
            this.currencyState = currencyValid ? null : false;
            let allValid = amountValid && currencyValid;
            this.localState = allValid ? null : false;
            return allValid;
        },
        checkInvalidMessage: function() { // our standard validity checker function
            /*
            can be called from parent if using ref="" and this.$refs....
            - check each child input using ref="" and !!.value or .reportValidity() or something else
            - add any group validity logic with an isValid computed prop
            check isValid, check child refs, return string per below:
            return '' empty string if isValid = true, return string with error message if not valid
            */
            const errorMessage = this.label + ': ' + 'Input is not valid';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        }
    },
    template: `
        <div>
            <b-form-group
                :id="'fieldset-' + idSuffix"
                class="hot-edit-input-group hot-edit-money mb-0"
                :class="{
                    'edit-mode': localEditMode,
                    'display-mode': !localEditMode,
                    'spl-inputs-loading': loading}"
                :label="label"
                :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
                :label-cols="labelVertical ? null : labelCols"
                :label-align="labelVertical ? 'left' : labelAlign"
                :state="localEditMode ? localState : null"
            >
                <template #label>
                    {{ label }}
                    <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                    <base-popover-icon
                        v-if="popoverContent && localEditMode"
                        class="ml-2"
                        :title="label"
                        :content="popoverContent"    
                    ></base-popover-icon>
                </template>

                <div
                    v-if="!localEditMode"
                    key="noedit"
                    class="mb-0 font-size-100"
                    :class="[noEditClass, {'ml-3': !labelVertical}]"
                >
                    <b-skeleton v-if="loading" key="noEditLoading" width="50%"></b-skeleton>
                    <div v-else key="noEditNotLoading">
                        <span v-if="!amount && emptyText" class="text-muted">{{ emptyText }}</span>
                        {{ amountFormatted }}&nbsp;
                        <span class="small text-black-50">{{ amount ? currency : '' }}</span>
                    </div>
                </div>

                <b-form v-else key="edit" inline>
                
                    <label class="sr-only" :for="'input-money-amount-' + idSuffix">{{ label + ' Amount' }}</label>
                    <b-form-input
                        :id="'input-money-amount-' + idSuffix"
                        ref="amount"
                        type="number"
                        number
                        step="0.01"
                        :required="required"
                        v-bind:value="amount"
                        v-on:input.native="updateAmount($event.target.value)" 
                        @blur="checkValidity"
                        trim
                        :disabled="disabled"
                        :plaintext="!localEditMode"
                        :state="amountState"
                        :class="{'no-outline': !localEditMode}"
                    ></b-form-input>

                    <label class="sr-only" :for="'input-money-currency-' + idSuffix">{{ label + ' Currency' }}</label>
                    <b-form-select   
                        :id="'input-money-currency-' + idSuffix"
                        ref="currency"
                        :required="required"
                        :disabled="disabled"
                        v-bind:value="currency"
                        v-on:input.native="updateCurrency($event.target.value)" 
                        :state="currencyState"
                        :options="currencyOptions"
                    >
                        <template v-slot:first>
                            <b-form-select-option v-if="loading" value="">
                                ...
                            </b-form-select-option>
                            <b-form-select-option v-else value="" disabled>
                                ---
                            </b-form-select-option>
                        </template>
                    </b-form-select>

                </b-form>

            </b-form-group>
        </div>
    `
}

var MoneyAmountInput = {
    // a plain HTML5 input that can handle v-model while displaying money correctly (with symbol)
    // cannot change currency... only amount
    // This works (as of 5/9/2020) but you may just want to use masked input for easier front-end settings, like user-adjustable decimal places, etc -- you're making separate component for that
    name: 'MoneyAmountInput',
    props: {
        value: [String, Number],  // supports v-model
        currency: {  // 3-digit currency code
            type: String,
            default: 'USD',
            validator: function (value) {
                // String length must be 3 characters
                return value.length === 3;
            }
        },
        locale: {  // locale string, like 'en-US'
            type: String,
            default: 'en-US',
        },
        placeholder: {  // the string to show when value is null (not zero)
            type: String,
            default: '---',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        size: {  // string for size of input, 'sm', 'md', 'lg'
            type: String,
            default: 'md',
        },
        decimals: {  
            // override the number of decimal digits allowed to input
            // only for input -- display will be governed by JS currency formatting
            // if not set, then default is 2, or default for a specific currency
            // must be between 0-4 (inclusive) max is 4 since that's what backend supports
            type: Number,
            required: false,
        }
    },
    data: function() {
        return {
            localPlaceholder: this.placeholder,
        }
    },
    computed: {
        localValueString: function() {
            // normalize our value prop and handle empty case
            return this.value ? this.value.toString() : '';  // so we know we're dealing with string (normalize is necessary?)
        },
        localValueNumber: function() {
            // take value prop (forced to String) and force/parse it to a Number
            // use attached method becuase you need to use same thing after updating data, before $emit to parent
            return this.forceToNumber(this.localValueString);
        },
        localValueForDisplay: function() {
            // force value to be a currency (for display)
            if (!this.localValueNumber) {
                return null;
            }
            const currencyFormat = {
                style: 'currency',
                currency: this.currency,
            };
            return this.localValueNumber.toLocaleString(this.locale, currencyFormat);
        },
        inputClasses: function() {
            switch (this.size) {
                case 'md':
                    return 'form-control';
                case 'sm':
                    return 'form-control form-control-sm';
                case 'lg':
                    return 'form-control form-control-lg';
                default:
                    return 'form-control';
            }
        },
        localDecimals: function() { // actual number of decimals to allow in input, based on currency and decimals props
            // if decimals prop is set, use that in all cases
            // if decimals prop is not set, then default to 2 decimals, unless certain currency is selected (like JPY) that don't have 2 decimals -- need to keep this list updated manually
            // absolute max is 4 since that's what backend supports
            const zeroDec = ['JPY'];
            if (this.decimals && this.decimals >= 0 && this.decimals <= 4) {
                return this.decimals;
            } else if (zeroDec.includes(this.currency)) {
                return 0;
            } else {
                return 2;
            }
        }
    },
    methods: {
        forceToNumber: function(value) {
            // must pass in value -- this is a method, not computed property
            // takes string or number, strips if needed, and returns number
            let numPattern = /[^0-9.-]+/g;
            if (!value) {
                return null;
            }
            // numValueRaw will be Number, but may have too many digits/decimal places
            var numValueRaw = Number.parseFloat(value.replace(numPattern, ''));
            if (Number.isNaN(numValueRaw)) {
                return null;
            } else {
                // use .toFixed to truncate decimals, returns a string, then parse back to a number
                return Number.parseFloat(numValueRaw.toFixed(this.localDecimals));
            }
        },
        handleChange: function(newValue) {
            // we'll emit 'input' so v-model works normally, but only do it on 'change' to prevent excessive updates (make it lazy)
            // newValue is raw... may have too many digits, too many decimals, etc.
            // so we need to validate it / coerce it to the right value
            // pass it through method this.forceToNumber() to do this before emitting
            // it should also truncate digits/decimals appropriately
            // don't set any localValue during this... $emit to parent, then parent will provide new value
            var newValueForceToNumber = this.forceToNumber(newValue);
            this.$emit('input', newValueForceToNumber);
            /*
            this.$nextTick(() => {
                console.log(this.localValueNumber);
                this.$emit('input', this.localValueNumber);
            });
            */
        }
    },
    template: `
    <input
        :value="localValueForDisplay"
        @change="handleChange($event.target.value)"
        @focus="localPlaceholder = ''"
        @blur="localPlaceholder = placeholder"
        class="text-right"
        :class="inputClasses"
        :disabled="disabled"
        :placeholder="localPlaceholder"
    >
    `
}

var OldMoneyAmountInput = {
    // Kept this for reference... but it doesn't handle updating data correctly... use MoneyAmountInput instead
    // a plain HTML5 input that can handle v-model while displaying money correctly (with symbol)
    // cannot change currency... only amount
    // This works (as of 5/9/2020) but you may just want to use masked input for easier front-end settings, like user-adjustable decimal places, etc -- you're making separate component for that
    name: 'MoneyAmountInput',
    props: {
        value: [String, Number],
        currency: {  // 3-digit currency code
            type: String,
            default: 'USD',
            validator: function (value) {
                // String length must be 3 characters
                return value.length === 3;
            }
        },
        locale: {  // locale string, like 'en-US'
            type: String,
            default: 'en-US',
        },
        placeholder: {  // the string to show when value is null (not zero)
            type: String,
            default: '---',
        },
        size: {  // string for size of input, 'sm', 'md', 'lg'
            type: String,
            default: 'md',
        },
        decimals: {  
            // override the number of decimal digits allowed to input
            // only for input -- display will be governed by JS currency formatting
            // if not set, then default is 2, or default for a specific currency
            // must be between 0-4 (inclusive) max is 4 since that's what backend supports
            type: Number,
            required: false,
        }
    },
    data: function() {
        return {
            localValue: this.value,  // our local v-model (deconstructed) will set this
            localPlaceholder: this.placeholder,
        }
    },
    computed: {
        localValueString: function() {
            // normalize our value prop and handle empty case
            return this.localValue ? this.localValue.toString() : '';  // so we know we're dealing with string
        },
        localValueNumber: function() {
            // take value prop and force/parse it to a Number
            let numPattern = /[^0-9.-]+/g;
            if (!this.localValueString) {
                return null;
            }
            // numValueRaw will be Number, but may have too many digits/decimal places
            var numValueRaw = Number.parseFloat(this.localValueString.replace(numPattern, ''));
            if (Number.isNaN(numValueRaw)) {
                return null;
            } else {
                // use .toFixed to truncate decimals, returns a string, then parse back to a number
                return Number.parseFloat(numValueRaw.toFixed(this.localDecimals));
            }
        },
        localValueForDisplay: function() {
            // force value to be a currency (for display)
            if (!this.localValueNumber) {
                return null;
            }
            const currencyFormat = {
                style: 'currency',
                currency: this.currency,
            };
            return this.localValueNumber.toLocaleString(this.locale, currencyFormat);
        },
        inputClasses: function() {
            switch (this.size) {
                case 'md':
                    return 'form-control';
                case 'sm':
                    return 'form-control form-control-sm';
                case 'lg':
                    return 'form-control form-control-lg';
                default:
                    return 'form-control';
            }
        },
        localDecimals: function() { // actual number of decimals to allow in input, based on currency and decimals props
            // if decimals prop is set, use that in all cases
            // if decimals prop is not set, then default to 2 decimals, unless certain currency is selected (like JPY) that don't have 2 decimals -- need to keep this list updated manually
            // absolute max is 4 since that's what backend supports
            const zeroDec = ['JPY'];
            if (this.decimals && this.decimals >= 0 && this.decimals <= 4) {
                return this.decimals;
            } else if (zeroDec.includes(this.currency)) {
                return 0;
            } else {
                return 2;
            }
        }
    },
    methods: {
        handleChange: function(newValue) {
            // we'll emit 'input' so v-model works normally, but only do it on 'change' to prevent excessive updates (make it lazy)
            // newValue is raw... may have too many digits, too many decimals, etc.
            // so we need to validate it / coerce it to the right value
            // this *should* happen through our chain of localValue --> localValueString --> localValueNumber
            // localValueNumber should also truncate digits/decimals appropriately
            this.localValue = newValue;
            this.$nextTick(() => {
                console.log(this.localValueNumber);
                this.$emit('input', this.localValueNumber);
            });
        }
    },
    template: `
    <input
        :value="localValueForDisplay"
        @change="handleChange($event.target.value)"
        @focus="localPlaceholder = ''"
        @blur="localPlaceholder = placeholder"
        class="text-right"
        :class="inputClasses"
        :placeholder="localPlaceholder"
    >
    `
}

var MoneyAmountMaskedInput = {
    // a plain HTML5 input that can handle v-model while displaying money correctly
    // USES MASKED INPUT
    // cannot change currency... only amount -- currency is a PROP
    // reference:  https://jsfiddle.net/Linusborg/36pm6bs9/
    // it works, but you're going back to non-masked input one... more control for you
    name: 'MoneyAmountMaskedInput',
    props: {
        value: [String, Number],
        currency: {  // 3-digit currency code
            type: String,
            default: 'USD',
        },
        locale: {  // locale string, like 'en-US'
            type: String,
            default: 'en-US',
        },
        placeholder: {  // the string to show when value is null (not zero)
            type: String,
            default: '---',
        },
        size: {  // string for size of input, 'sm', 'md', 'lg'
            type: String,
            default: 'md',
        }
    },
    data: function() {
        return {
            localValue: this.value,
            cleaveLocal: null,
        }
    },
    computed: {
        localValueString: function() {
            // normalize our value prop and handle empty case
            return this.localValue ? this.localValue.toString() : '';  // so we know we're dealing with string
        },
        localValueNumber: function() {
            // take value prop and force/parse it to a Number
            let numPattern = /[^0-9.-]+/g;
            if (!this.localValueString) {
                return null;
            }
            const numValue = parseFloat(this.localValueString.replace(numPattern, ''));
            if (Number.isNaN(numValue)) {
                return null;
            } else {
                return numValue;
            }
        },
        localValueForDisplay: function() {
            // force value to be a currency (for display)
            if (!this.localValueNumber) {
                return null;
            }
            const currencyFormat = {
                style: 'currency',
                currency: this.currency,
            };
            return this.localValueNumber.toLocaleString(this.locale, currencyFormat);
        },
        inputClasses: function() {
            switch (this.size) {
                case 'md':
                    return 'form-control';
                case 'sm':
                    return 'form-control form-control-sm';
                case 'lg':
                    return 'form-control form-control-lg';
                default:
                    return 'form-control';
            }
        }
    },
    watch: {
        // similar result to doing v-model... but we can set rawValue directly this way
        value: 'updateInput'
    },
    mounted: function() {
        const cleaveOptions = {
            numeral: true,
            prefix: '$ ',
            rawValueTrimPrefix: true,
            noImmediatePrefix: true,
            numeralDecimalScale: 4
        }
        this.cleave = new Cleave(this.$el, cleaveOptions);
        //this.cleave.setRawValue(this.value);  // set to initial value prop
        //this.cleaveLocal = this.cleave.getRawValue();
        this.setCleave();
    },
    destroyed: function() {
        this.cleave.destroy();
    },
    methods: {
        /*
        handleValueChanged: function(evt) {
            // we'll emit 'input' so v-model works normally
            // this is called by Cleave.js with arg evt, like this: evt.target = { value: '5100-1234', rawValue: '51001234' }
            console.log("evt from handleValueChanged is below");
            console.log(evt);
            const newRawValue = evt.target.rawValue;
            this.localValue = newRawValue;
            this.$emit('input', newRawValue);
        },*/
        setCleave: function() {
            // set cleave rawValue (and cleaveLocal) based on value prop
            this.cleaveLocal = this.cleave.setRawValue(Number.parseFloat(this.value)); 
        },
        getCleave: function() {
            var getVal = Number.parseFloat(this.cleave.getRawValue());
            this.cleaveLocal = getVal;
            return getVal;
        },
        updateValue: function() {
            // reports up our new value when edited by user
            // var val = this.cleave.getRawValue();
            // this.cleaveLocal = val;
            var val = this.getCleave();
            console.log("from updateValue, cleave rawValue should be: " + val);
            if (val !== this.value) {
                this.$emit('input', val);
            }
        },
        updateInput: function() {
            // used to assing value to rawValue when parent updates value prop
            // this.cleave.setRawValue(this.value);
            // this.cleaveLocal = this.value;
            this.setCleave();
        }
    },
    template: `
    <input
        @change="updateValue"
        :placeholder="placeholder"
        class="text-right"
        :class="inputClasses"
    >
    `
}


export {
    HotEditInput,
    HotEditDiv,
    HotEditSelect,
    HotEditCheckboxGroup,
    HotEditTextarea,
    HotEditSwitch,
    HotEditMoney,
    HotEditDatepicker,
    HotEditDateTime,
    ClickEditInput,
    MoneyAmountInput,
    OldMoneyAmountInput,
    MoneyAmountMaskedInput,
}